// @flow
import invariant from 'invariant';
import { getJson } from 'utils/http';
import algoliaSearch from 'utils/algoliaSearch';
import { getStaffSearchQueryOptions } from 'utils/search/getSearchQueryOptions';
import { getIn } from 'utils/functional';
import { getDefaultSearchRadius } from 'utils/constants';
import getSearchConfig from 'utils/search/getSearchConfig';
import { fetchCurrentUser } from 'api/currentUser';
import { updateCurrentUser } from '../../main/actions/currentUser';
import { setSearchRadius as setSearchRadiusBase } from '../../main/actions/searchRadius';
import type { State, Dispatch, GetState } from '..';
import type { SearchResults } from '../reducers/search';

type InitializeAction = {
  type: 'SEARCH_INITIALIZE',
  queryParams: Object
};
type ResetPageAction = { type: 'SEARCH_RESET_PAGE' };
type SetQueryAction = { type: 'SEARCH_SET_QUERY', query: string };
type SetPageIndexAction = { type: 'SEARCH_SET_PAGE_INDEX', pageIndex: number };
type SetLocationAction = {
  type: 'SEARCH_SET_LOCATION',
  location: SearchLocation
};
type ClearLocationAction = {
  type: 'SEARCH_CLEAR_LOCATION'
};
type ResetFiltersAction = {
  type: 'SEARCH_RESET_FILTERS'
};
type SetFilterAction = {
  type: 'SEARCH_SET_FILTER',
  key: string,
  value: ?string
};
type SelectFilterOptionAction = {
  type: 'SEARCH_SELECT_FILTER_OPTION',
  key: string,
  option: ?string
};
type DeselectFilterOptionAction = {
  type: 'SEARCH_DESELECT_FILTER_OPTION',
  key: string,
  option: ?string
};
type SelectFilterSpecificOptionsAction = {
  type: 'SEARCH_SELECT_FILTER_SPECIFIC_OPTIONS',
  key: string,
  options: Array<string>
};
type SelectAllFilterOptionsAction = {
  type: 'SEARCH_SELECT_ALL_FILTER_OPTIONS',
  key: string
};
type SetSearchFacetOptionsAction = {
  type: 'SET_SEARCH_FACET_OPTIONS',
  options: Array<string>,
  optionTitles: Array<string>,
  facetKey: string,
  optionRankings: Object
};
type ClearResultsAction = {
  type: 'SEARCH_CLEAR_RESULTS'
};
type SearchStartedAction = { type: 'SEARCH_STARTED' };
type SuccessAction = {
  type: 'SEARCH_RESULTS_SUCCESS',
  results: SearchResults,
  hasOrgResults?: boolean
};
type FetchPageSuccessAction = {
  type: 'SEARCH_FETCH_PAGE_SUCCESS',
  results: SearchResults
};

export type Action =
  | InitializeAction
  | ResetPageAction
  | SetQueryAction
  | SetPageIndexAction
  | SetLocationAction
  | ClearLocationAction
  | ResetFiltersAction
  | SetFilterAction
  | SelectFilterOptionAction
  | DeselectFilterOptionAction
  | SelectFilterSpecificOptionsAction
  | SelectAllFilterOptionsAction
  | SetSearchFacetOptionsAction
  | ClearResultsAction
  | SearchStartedAction
  | SuccessAction
  | FetchPageSuccessAction;

const searchResultsSuccessful = ({
  results
}: {
  results: SearchResults
}): SuccessAction => ({
  type: 'SEARCH_RESULTS_SUCCESS',
  results
});

const executeSearchQuery = ({
  state: {
    search,
    currentUser,
    searchRadius: { value: searchRadius }
  },
  dispatch
}: {
  state: State,
  dispatch: Dispatch
}): Promise<*> => {
  const timestamp = new Date();

  const queryOptions = getStaffSearchQueryOptions(search.searchFacets, {
    location: search.searchLocation,
    radius: searchRadius,
    ...search
  });

  const searchConfig = getSearchConfig(
    getIn(currentUser, ['extraData', 'staffSearchApiKey'])
  );
  return algoliaSearch(searchConfig, search.query, queryOptions, true, () =>
    fetchCurrentUser().then(fetchedCurrentUser => {
      dispatch(updateCurrentUser(fetchedCurrentUser));
      return fetchedCurrentUser.extraData.staffSearchApiKey;
    })
  ).then(res => ({
    // Add a timestamp to keep track of when the query was executed
    ...res,
    // Add the searchLocation so we know which location the results are for
    searchLocation: search.searchLocation,
    searchId: search.searchId,
    timestamp,
    // This is for analytics
    searchParameters: {
      query: search.query,
      queryOptions
    }
  }));
};

const loadDynamicFacetOptions = () => (
  dispatch: Dispatch,
  getState: GetState
) => {
  const { facetsThatArentLoaded } = getState().search;
  return Promise.all(
    [
      {
        facetUrlSegment: 'job-functions',
        facetKey: 'jobKeywordsFacet',
        hasLang: true,
        responseToOptions: ({ jobFunctions }) => jobFunctions
      },
      {
        facetUrlSegment: 'areas-of-focus',
        facetKey: 'orgAreasOfFocusFacet',
        hasLang: true,
        responseToOptions: ({ areasOfFocus }) => areasOfFocus
      },
      {
        facetUrlSegment: 'volop-functions',
        facetKey: 'volopKeywordsFacet',
        hasLang: true,
        responseToOptions: ({ volopFunctions }) => volopFunctions
      },
      {
        facetUrlSegment: 'internship-functions',
        facetKey: 'internshipKeywordsFacet',
        hasLang: true,
        responseToOptions: ({ internshipFunctions }) => internshipFunctions
      },
      {
        facetUrlSegment: 'idealist-day-dates',
        facetKey: 'idealistDayFacet',
        hasLang: false,
        responseToOptions: ({ dates }) => dates
      }
    ].map(({ facetUrlSegment, facetKey, hasLang, responseToOptions }) => {
      if (!facetsThatArentLoaded.includes(facetKey)) {
        return Promise.resolve();
      }
      return getJson(
        `/data/website/${facetUrlSegment}${
          hasLang ? `?lang=${USER_LOCALE}` : ''
        }`
      ).then(response => {
        const options = responseToOptions(response);
        dispatch({
          type: 'SET_SEARCH_FACET_OPTIONS',
          facetKey,
          options: options.map(f => f.id),
          optionTitles: options.reduce(
            (sum, { id, text }) => ({
              ...sum,
              [id]: text
            }),
            {}
          ),
          optionRankings: options.reduce(
            (sum, { id, usageRanking }) => ({
              ...sum,
              [id]: usageRanking
            }),
            {}
          )
        });
      });
    })
  );
};

export const initializeSearch = (action: Object = {}) => (dispatch: Dispatch) =>
  Promise.all([
    dispatch({
      type: 'SEARCH_INITIALIZE',
      ...action
    }),
    dispatch(loadDynamicFacetOptions())
  ]);

export const searchResetPage = (): ResetPageAction => ({
  type: 'SEARCH_RESET_PAGE'
});

export const setSearchQuery = (query: string) => ({
  type: 'SEARCH_SET_QUERY',
  query
});

export const setSearchRadius = (radius: string, didUserChange: boolean) => (
  dispatch: Dispatch
) => {
  // $FlowFixMe: this uses a *different* Dispatch -- the user dispatch from store.js
  dispatch(setSearchRadiusBase(radius, didUserChange));
  dispatch(searchResetPage());
};

export const setPageIndex = (pageIndex: number) => ({
  type: 'SEARCH_SET_PAGE_INDEX',
  pageIndex
});

export const updateSearchLocation = (location: SearchLocation) => ({
  type: 'SEARCH_SET_LOCATION',
  location
});

export const clearSearchLocation = () => ({
  type: 'SEARCH_CLEAR_LOCATION'
});

export const resetSearchFilters = (): ResetFiltersAction => ({
  type: 'SEARCH_RESET_FILTERS'
});

export const clearAllFilters = () => (dispatch: Dispatch) => {
  dispatch(resetSearchFilters());
  dispatch(setSearchRadius(getDefaultSearchRadius('relevance'), false));
};

export const setSearchFilter = (filterKey: string, value: ?string) => {
  if (filterKey === 'type') {
    invariant(value, 'type can never be set to undefined/null');
  }
  return {
    type: 'SEARCH_SET_FILTER',
    key: filterKey,
    value
  };
};

export const selectSearchFilterOption = (
  filterKey: string,
  option: ?string
) => ({ type: 'SEARCH_SELECT_FILTER_OPTION', key: filterKey, option });

export const deselectSearchFilterOption = (
  filterKey: string,
  option: ?string
) => ({ type: 'SEARCH_DESELECT_FILTER_OPTION', key: filterKey, option });

export const selectAllSearchFilterOptions = (filterKey: string) => ({
  type: 'SEARCH_SELECT_ALL_FILTER_OPTIONS',
  key: filterKey
});

export const selectSearchFilterSpecificOptions = (
  filterKey: string,
  options: Array<string>
) => ({
  type: 'SEARCH_SELECT_FILTER_SPECIFIC_OPTIONS',
  key: filterKey,
  options
});

export const search = () => (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: 'SEARCH_STARTED' });
  const state = getState();

  executeSearchQuery({ state, dispatch })
    .then(results => {
      dispatch(searchResultsSuccessful({ results }));
    })
    .catch(error => error);
};

export const clearResults = (): ClearResultsAction => ({
  type: 'SEARCH_CLEAR_RESULTS'
});
